import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import {
  ChatHistoryItem,
  ChatStreamEvent,
  IChatAttachment,
  IChatCard,
  IChatContext,
  IChatQuestions,
  IChatCreated,
  IConversation,
  IConversationSummary,
  IProposal,
  ProposalDecision,
  ProposalStatus,
} from 'types/domain/chat';
import { switchUser } from 'core/actions/product/state';
import { logout } from 'core/slices/session';
import { pastTense } from 'utils/pastTense';

/**
 * The in-app assistant: whether the window is showing, the thread, and the turn
 * in flight. The stream itself, and everything it touches outside this slice,
 * lives in core/sagas/admin/chat.
 */

export type ToolStatus = 'running' | 'ok' | 'error' | 'stopped';

export type ChatItem =
  | { kind: 'user'; id: string; text: string; attachments: string[] }
  | { kind: 'text'; id: string; text: string }
  | { kind: 'tool'; id: string; toolId: string; name: string; summary: string; status: ToolStatus }
  | {
      kind: 'proposal';
      id: string;
      proposal: IProposal;
      status: ProposalStatus;
      /** The decision sent and not yet answered. */
      deciding: ProposalDecision | null;
    }
  | { kind: 'card'; id: string; card: IChatCard }
  /** Answered once a user message follows it: the answers are that message. */
  | { kind: 'questions'; id: string; questions: IChatQuestions }
  | { kind: 'done'; id: string; text: string }
  | { kind: 'error'; id: string; message: string };

export type ChatItemKind = ChatItem['kind'];

export type ChatView = 'chat' | 'history';

export interface IChatHistory {
  list: IConversationSummary[];
  loading: boolean;
  failed: boolean;
}

export interface IChatState {
  open: boolean;
  /** The thread, or the list of past chats (AI-07). */
  view: ChatView;
  history: IChatHistory;
  /** Collapsed to the launcher with the thread (and any stream) kept. */
  minimised: boolean;
  conversationId: string | null;
  items: ChatItem[];
  streaming: boolean;
  /** The proposal the turn is paused on, if any. */
  pendingProposalId: string | null;
  /** The confirmed proposal whose continuation is streaming — its done line is added when it ends cleanly. */
  confirming: { proposalId: string; failed: boolean } | null;
  /** The last conversation has been looked for this session (once per page load). */
  restored: boolean;
  restoring: boolean;
  /** The past chat being opened from the history; a restore of any other is dropped. */
  opening: string | null;
  /** Text to put in the message box when the window shows (e.g. a decision's action on the Sources page). */
  prefill: string | null;
  /** Bumped when the assistant changes learners; lists that show them re-fetch on change. */
  refresh: { learners: number };
  /**
   * Something the assistant created that the app should open (AiChat navigates, then clears it).
   * Per turn, the first thing created wins, except that a new course beats the modules added to it.
   */
  openTarget: IChatCreated | null;
  /** What this turn has already opened, so building a course doesn't hop from module to module. */
  openedThisTurn: IChatCreated['kind'] | null;
  /**
   * Modules a course build laid out empty and hasn't filled yet (ids as strings). Their steps show
   * as being built while a turn streams. Kept past a turn cut short, so "carry on" picks them up.
   */
  building: string[];
}

export const initialState: IChatState = {
  open: false,
  view: 'chat',
  history: { list: [], loading: false, failed: false },
  minimised: false,
  conversationId: null,
  items: [],
  streaming: false,
  pendingProposalId: null,
  confirming: null,
  restored: false,
  restoring: false,
  opening: null,
  prefill: null,
  refresh: { learners: 0 },
  openTarget: null,
  openedThisTurn: null,
  building: [],
};

export interface ISendMessage {
  text: string;
  context: IChatContext;
  attachments?: IChatAttachment[];
}

const LIMIT_MESSAGES = {
  max_tool_calls: 'I reached the most steps I can take in one go. Ask me to carry on.',
  max_tokens: 'My answer got too long and was cut short. Ask me to carry on.',
};

const stopRunningTools = (state: IChatState, status: ToolStatus) => {
  state.items.forEach(item => {
    if (item.kind === 'tool' && item.status === 'running') item.status = status;
  });
};

const findTool = (state: IChatState, toolId: string) =>
  state.items.find((i): i is Extract<ChatItem, { kind: 'tool' }> => i.kind === 'tool' && i.toolId === toolId);

const findProposal = (state: IChatState, proposalId: string) =>
  state.items.find(
    (i): i is Extract<ChatItem, { kind: 'proposal' }> => i.kind === 'proposal' && i.proposal.id === proposalId,
  );

const endTurn = (state: IChatState) => {
  state.streaming = false;
  state.confirming = null;
  state.items.forEach(item => {
    if (item.kind === 'proposal') item.deciding = null;
  });
};

/** Rebuilds the thread from `GET /api/ai/conversations/{id}`. */
export function fromHistory(messages: ChatHistoryItem[]): ChatItem[] {
  const items: ChatItem[] = [];
  // A confirmed proposal's done line closes its turn, as it does live: after the
  // tool lines and reply that carried it out, and only if none of them failed.
  let done: { text: string; failed: boolean } | null = null;
  const closeTurn = () => {
    if (done && !done.failed) items.push({ kind: 'done', id: nanoid(), text: done.text });
    done = null;
  };
  for (const m of messages ?? []) {
    if (m.type === 'user') {
      closeTurn();
      items.push({
        kind: 'user',
        id: nanoid(),
        text: m.text ?? m.message ?? '',
        attachments: (m.attachments ?? []).map(a => a.name),
      });
    } else if (m.type === 'text') {
      const text = m.text ?? m.delta ?? '';
      const last = items[items.length - 1];
      if (last?.kind === 'text') last.text += text;
      else if (text) items.push({ kind: 'text', id: nanoid(), text });
    } else if (m.type === 'tool') {
      items.push({
        kind: 'tool',
        id: nanoid(),
        toolId: m.id,
        name: m.name,
        summary: m.summary,
        status: m.ok === false ? 'error' : 'ok',
      });
      if (done && m.ok === false) done.failed = true;
    } else if (m.type === 'proposal') {
      closeTurn();
      const { type, status, ...proposal } = m;
      items.push({
        kind: 'proposal',
        id: nanoid(),
        proposal: proposal as IProposal,
        status: status ?? 'pending',
        deciding: null,
      });
      if (status === 'confirmed') done = { text: pastTense(proposal.title), failed: false };
    } else if (m.type === 'card') {
      const { type, ...card } = m;
      items.push({ kind: 'card', id: nanoid(), card });
    } else if (m.type === 'questions') {
      const { type, ...questions } = m;
      items.push({ kind: 'questions', id: nanoid(), questions });
    }
  }
  closeTurn();
  return items;
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    openChat: state => {
      state.open = true;
      state.minimised = false;
    },
    minimiseChat: state => {
      state.minimised = true;
    },
    /** Ends the conversation: the saga aborts any stream, and the next open starts fresh. */
    closeChat: state => ({ ...initialState, restored: true, refresh: state.refresh }),
    /** An empty thread, the window kept open; the saga aborts any stream. The next message carries the current page. */
    newChat: state => ({ ...initialState, open: true, restored: true, refresh: state.refresh, history: state.history }),
    /** Swaps the thread for the list of past chats; the saga fetches it. */
    showHistory: state => {
      state.view = 'history';
      state.history.loading = true;
      state.history.failed = false;
    },
    showThread: state => {
      state.view = 'chat';
    },
    historyLoaded: (state, action: PayloadAction<IConversationSummary[]>) => {
      state.history = { list: action.payload, loading: false, failed: false };
    },
    historyFailed: state => {
      state.history.loading = false;
      state.history.failed = true;
    },
    /** Opens a past chat: the thread empties, and restoreFinished fills it with that chat and any pending change. */
    openConversation: (state, action: PayloadAction<string>) => {
      if (state.conversationId === action.payload) {
        state.view = 'chat';
        return;
      }
      return {
        ...initialState,
        open: true,
        restored: true,
        restoring: true,
        opening: action.payload,
        refresh: state.refresh,
        history: state.history,
      };
    },
    sendMessage: {
      reducer: (state, action: PayloadAction<ISendMessage, string, { id: string }>) => {
        if (state.streaming) return;
        state.items.push({
          kind: 'user',
          id: action.meta.id,
          text: action.payload.text,
          attachments: (action.payload.attachments ?? []).map(a => a.name),
        });
        // Typing over a pending proposal moves on from it; the server treats the
        // paused turn as superseded and the card stays readable but inert.
        if (state.pendingProposalId) {
          const p = findProposal(state, state.pendingProposalId);
          if (p && p.status === 'pending') p.status = 'discarded';
          state.pendingProposalId = null;
        }
        state.streaming = true;
        state.openedThisTurn = null;
        state.openedThisTurn = null;
        state.confirming = null;
      },
      prepare: (payload: ISendMessage) => ({ payload, meta: { id: nanoid() } }),
    },
    decideProposal: (state, action: PayloadAction<{ proposalId: string; decision: ProposalDecision }>) => {
      const p = findProposal(state, action.payload.proposalId);
      if (!p || p.status !== 'pending' || state.streaming) return;
      p.deciding = action.payload.decision;
      p.status = action.payload.decision === 'confirm' ? 'confirmed' : 'discarded';
      state.pendingProposalId = null;
      state.streaming = true;
      state.confirming = action.payload.decision === 'confirm' ? { proposalId: p.proposal.id, failed: false } : null;
    },
    /** The decision request failed before the server took it: let the user try again. */
    decisionFailed: (state, action: PayloadAction<{ proposalId: string; message: string }>) => {
      const p = findProposal(state, action.payload.proposalId);
      if (p) {
        p.status = 'pending';
        p.deciding = null;
        state.pendingProposalId = p.proposal.id;
      }
      state.items.push({ kind: 'error', id: nanoid(), message: action.payload.message });
      endTurn(state);
    },
    /** The server no longer knows the proposal (404): it can't be decided, so it is set aside. */
    decisionRejected: (state, action: PayloadAction<{ proposalId: string; message: string }>) => {
      const p = findProposal(state, action.payload.proposalId);
      if (p) p.status = 'discarded';
      state.items.push({ kind: 'error', id: nanoid(), message: action.payload.message });
      endTurn(state);
    },
    stopStreaming: state => {
      stopRunningTools(state, 'stopped');
      state.building = [];
      endTurn(state);
    },
    streamEvent: (state, action: PayloadAction<ChatStreamEvent>) => {
      const e = action.payload;
      switch (e.event) {
        case 'conversation':
          state.conversationId = e.data.conversationId;
          break;
        case 'text': {
          const last = state.items[state.items.length - 1];
          if (last?.kind === 'text') last.text += e.data.delta;
          else state.items.push({ kind: 'text', id: nanoid(), text: e.data.delta });
          break;
        }
        case 'tool_start': {
          // A write is announced while the model is still writing it, then started again
          // with its full summary when it runs: the same line, updated.
          const tool = findTool(state, e.data.id);
          if (tool) {
            tool.summary = e.data.summary || tool.summary;
            tool.status = 'running';
          } else {
            state.items.push({
              kind: 'tool',
              id: nanoid(),
              toolId: e.data.id,
              name: e.data.name,
              summary: e.data.summary,
              status: 'running',
            });
          }
          break;
        }
        case 'tool_end': {
          const tool = findTool(state, e.data.id);
          const status: ToolStatus = e.data.ok ? 'ok' : 'error';
          if (tool) {
            tool.status = status;
            if (e.data.summary) tool.summary = e.data.summary;
          } else {
            state.items.push({
              kind: 'tool',
              id: nanoid(),
              toolId: e.data.id,
              name: e.data.name,
              summary: e.data.summary,
              status,
            });
          }
          if (!e.data.ok && state.confirming) state.confirming.failed = true;
          if (e.data.ok && e.data.building) {
            const done = new Set((e.data.building.done ?? []).map(String));
            const add = (e.data.building.add ?? []).map(String);
            state.building = [...state.building, ...add.filter(id => !state.building.includes(id))].filter(
              id => !done.has(id),
            );
          }
          const created = e.data.ok ? e.data.created : null;
          if (
            created &&
            (created.kind === 'course' || created.courseId != null) &&
            state.openedThisTurn !== 'course' &&
            !(created.kind === 'module' && state.openedThisTurn === 'module')
          ) {
            state.openTarget = created;
            state.openedThisTurn = created.kind;
          }
          break;
        }
        case 'proposal':
          state.items.push({ kind: 'proposal', id: nanoid(), proposal: e.data, status: 'pending', deciding: null });
          state.pendingProposalId = e.data.id;
          break;
        case 'card':
          state.items.push({ kind: 'card', id: nanoid(), card: e.data });
          break;
        case 'questions':
          state.items.push({ kind: 'questions', id: nanoid(), questions: e.data });
          break;
        case 'done': {
          stopRunningTools(state, 'ok');
          if (state.confirming && !state.confirming.failed) {
            const p = findProposal(state, state.confirming.proposalId);
            if (p) state.items.push({ kind: 'done', id: nanoid(), text: pastTense(p.proposal.title) });
          }
          const limit = LIMIT_MESSAGES[e.data.stopReason as keyof typeof LIMIT_MESSAGES];
          if (limit) state.items.push({ kind: 'error', id: nanoid(), message: limit });
          // Cut short by a limit, the build carries on when asked; otherwise it is over.
          else state.building = [];
          endTurn(state);
          break;
        }
        case 'error':
          stopRunningTools(state, 'error');
          state.building = [];
          state.items.push({ kind: 'error', id: nanoid(), message: e.data.message });
          endTurn(state);
          break;
      }
    },
    /** The stream failed outside the protocol (network, HTTP status, closed without `done`). */
    streamFailed: (state, action: PayloadAction<string>) => {
      stopRunningTools(state, 'error');
      state.building = [];
      state.items.push({ kind: 'error', id: nanoid(), message: action.payload });
      endTurn(state);
    },
    restoreStarted: state => {
      state.restored = true;
      state.restoring = true;
    },
    restoreFinished: (state, action: PayloadAction<IConversation | null>) => {
      if (state.opening && action.payload?.id !== state.opening) return;
      state.restoring = false;
      state.opening = null;
      // The user started typing before the history arrived: theirs wins.
      if (!action.payload || state.items.length || state.conversationId) return;
      state.conversationId = action.payload.id;
      state.items = fromHistory(action.payload.messages);
      const pending = [...state.items]
        .reverse()
        .find((i): i is Extract<ChatItem, { kind: 'proposal' }> => i.kind === 'proposal' && i.status === 'pending');
      state.pendingProposalId = pending?.proposal.id ?? null;
    },
    /** A past chat couldn't be loaded: an empty thread with the reason, so the next message starts a new chat. */
    openFailed: (state, action: PayloadAction<string>) => {
      state.restoring = false;
      state.opening = null;
      state.items.push({ kind: 'error', id: nanoid(), message: action.payload });
    },
    /** Opens the window with `text` in the message box, for the user to edit and send. */
    prefillChat: (state, action: PayloadAction<string>) => {
      state.open = true;
      state.minimised = false;
      state.view = 'chat';
      state.prefill = action.payload;
    },
    prefillTaken: state => {
      state.prefill = null;
    },
    learnersChanged: state => {
      state.refresh.learners += 1;
    },
    openTargetHandled: state => {
      state.openTarget = null;
    },
  },
  extraReducers: builder => {
    // Chats belong to one user. Signing out or switching user doesn't reload the
    // page, so drop the thread here; the next open restores that user's own.
    const reset = () => initialState;
    builder.addCase(logout, reset).addCase(switchUser, reset);
  },
});

export const chatActions = chatSlice.actions;

export const {
  openChat,
  minimiseChat,
  closeChat,
  newChat,
  showHistory,
  showThread,
  openConversation,
  prefillChat,
  sendMessage,
  decideProposal,
  stopStreaming,
  streamEvent,
} = chatSlice.actions;

export default chatSlice.reducer;
